import { useEffect, useMemo, useState, useContext } from 'react';
import { UserContext } from '../../../context';
import { useSnackbar } from 'notistack';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { Button, Divider, FormControlLabel, Grid, Switch, useMediaQuery } from '@mui/material';
import { Add, Delete, Edit, Restore } from '@mui/icons-material';
import { Link, useHistory } from 'react-router-dom';
import {
  OrderManagementPage,
  PageTitle,
  PageTitleWrapper,
  Pagination,
  Table
} from '../../../components';
import { IDateRange, IFieldWalkthrough, IFieldWalkthroughTerritory } from '../../../models';
import {
  deleteFieldWalkthrough,
  getFieldWalkthroughs,
  resetFieldWalkthrough
} from '../../../fetch/field-walkthroughs';
import { formatDate } from '../../../helpers';
import { MobileFieldWalkthroughs } from './mobile-field-walkthroughs';
import { FieldWalkthroughFilters } from './field-walkthroughs-filters';

export const FieldWalkthroughsList = () => {
  const classes = useStyles();
  const { user, isProjectManager } = useContext(UserContext);
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const [queryString, setQueryString] = useState('');
  const isMobile = useMediaQuery('(max-width: 1199px)');
  const showToolbarDivider = useMediaQuery('(min-width: 600px)');
  const isAdmin = user?.userRoles?.includes('BL_Admin');
  const isFieldAdmin = user?.userRoles?.includes('BL_FieldAdmin');
  const hasRequiredRole = isAdmin || isFieldAdmin;

  const [isLoadingWalkthroughs, setIsloadingWalkthroughs] = useState(false);
  const [isComplete, toggleCompleted] = useState(false);
  const [fieldWalkthroughs, setFieldWalkthroughs] = useState<IFieldWalkthrough[]>([]);
  const [page, setPage] = useState<number>(0);
  const [recordCount, setRecordCount] = useState<number>(0);
  const [perPage, setRowsPerPage] = useState<number>(10);
  const [selectedSort, setSelectedSort] = useState<string>('NeighborhoodDescription');
  const [sortDirection, setSortDirection] = useState<{
    NeighborhoodDescription?: 'Asc' | 'Desc';
    LotNumber?: 'Asc' | 'Desc';
    PlanDescription?: 'Asc' | 'Desc';
    CreatedOn?: 'Asc' | 'Desc';
    CreatedBy?: 'Asc' | 'Desc';
  }>({ NeighborhoodDescription: 'Asc' });
  const [hasAppliedFilters, setHasAppliedFilters] = useState(false);
  const [filters, setFilters] = useState<{
    planDescription?: string;
    territoryId?: string | number | null;
    neighborhoodId?: string | number | null;
    lotNumber?: string | number | null;
    createdOnStart?: string;
    createdOnEnd?: string;
    createdBy?: string | null;
    isComplete?: boolean;
  }>({
    isComplete: false
  });
  const [searchValue, setSearchValue] = useState<string>('');
  const [newFilters, setNewFilters] = useState<any>({});
  const [selectedTerritory, setSelectedTerritory] = useState<IFieldWalkthroughTerritory | null>(
    null
  );
  const [selectedNeighborhood, setSelectedNeighborhood] = useState<string | null>(null);
  const [selectedLotNumber, setSelectedLotNumber] = useState<string | null>(null);
  const [selectedDateRange, setSelectedDateRange] = useState<IDateRange>({
    startDate: null,
    endDate: null,
    key: 'selection',
    inputValue: ''
  });
  const [selectedCreatedBy, setSelectedCreatedBy] = useState<string | null>(null);
  const [showOnlyMine, setShowOnlyMine] = useState(false);
  const [isResetting, setResetting] = useState(false);

  const handleClickColumn = (column: string) => {
    setSelectedSort(column);
    setSortDirection({
      ...sortDirection,
      [column]: sortDirection[column] === 'Asc' ? 'Desc' : 'Asc'
    });

    setNewFilters({ ...newFilters, sortBy: column, sortDirection: sortDirection[column] });
    updateUrlWithFilters({
      ...newFilters,
      sortBy: column,
      sortDirection: sortDirection[column] === 'Asc' ? 'Desc' : 'Asc'
    });
  };

  const updateUrlWithFilters = newFilters => {
    const queryParams = new URLSearchParams();
    Object.keys(newFilters).forEach(key => {
      if (newFilters[key]) {
        queryParams.set(key, newFilters[key]);
      }
    });
    const newQueryString = queryParams.toString();
    setQueryString(newQueryString);
    history.push({ search: newQueryString });
  };

  const formatDateRange = (startDate: Date, endDate: Date) => {
    const startDay = startDate.getDate();
    const startMonth = startDate.getMonth() + 1;
    const startYear = startDate.getFullYear();

    const endDay = endDate.getDate();
    const endMonth = endDate.getMonth() + 1;
    const endYear = endDate.getFullYear();

    const formattedDateRange = `${startMonth}/${startDay}/${startYear} - ${endMonth}/${endDay}/${endYear}`;

    return formattedDateRange;
  };

  const fetchFieldWalkthroughs = async () => {
    try {
      setIsloadingWalkthroughs(true);
      const res = await getFieldWalkthroughs({
        page: page + 1,
        perPage,
        sortBy: selectedSort,
        sortDirection: sortDirection[selectedSort] as string,
        showMine: showOnlyMine ?? false,
        ...filters
      });
      setFieldWalkthroughs(res.records);
      setRecordCount(res.totalRecordCount);
    } catch (error) {
      enqueueSnackbar(error?.Detail || `Error loading field walkthroughs, please try again.`, {
        variant: 'error'
      });
    } finally {
      setIsloadingWalkthroughs(false);
    }
  };

  useEffect(() => {
    if (!isLoadingWalkthroughs) return;

    const queryParams = new URLSearchParams(window.location.search);
    const urlFilters: any = {};

    queryParams.forEach((value, key) => {
      urlFilters[key] = value;
    });
    const newQueryString = queryParams.toString();
    setQueryString(newQueryString);

    if (Object.keys(urlFilters).length > 0) {
      const newFilters: any = {};

      if (urlFilters.planDescription) {
        newFilters.planDescription = urlFilters.planDescription;
        setSearchValue(urlFilters.planDescription);
      }
      if (urlFilters.territoryId) {
        newFilters.territoryId = urlFilters.territoryId;
        setSelectedTerritory({ territoryId: urlFilters.territoryId, territoryName: '' });
      }
      if (urlFilters.neighborhoodId) {
        newFilters.neighborhoodId = urlFilters.neighborhoodId;
        setSelectedNeighborhood(urlFilters.neighborhoodId);
      }
      if (urlFilters.lotNumber) {
        newFilters.lotNumber = urlFilters.lotNumber;
        setSelectedLotNumber(urlFilters.lotNumber);
      }
      if (urlFilters.createdOnStart) {
        const startDate = new Date(urlFilters.createdOnStart);
        newFilters.createdOnStart = startDate;
        const endDate = new Date(urlFilters.createdOnEnd);
        newFilters.createdOnEnd = endDate;
        setSelectedDateRange(prev => ({
          ...prev,
          startDate,
          endDate,
          inputValue: formatDateRange(startDate, endDate)
        }));
      }
      if (urlFilters.createdBy) {
        newFilters.createdBy = urlFilters.createdBy;
        setSelectedCreatedBy(urlFilters.createdBy);
      }
      if (urlFilters.isComplete !== undefined) {
        newFilters.isComplete = urlFilters.isComplete === 'true';
        toggleCompleted(urlFilters.isComplete === 'true');
      }
      if (urlFilters.page && Number(urlFilters.page) > 1) {
        newFilters.page = Number(urlFilters.page) - 1;
        setPage(Math.max(0, Number(urlFilters.page) - 1));
      } else {
        newFilters.page = 1;
        setPage(0);
      }
      if (urlFilters.perPage) {
        newFilters.perPage = Number(urlFilters.perPage);
        setRowsPerPage(Number(urlFilters.perPage));
      }
      if (urlFilters.sortBy) {
        newFilters.sortBy = urlFilters.sortBy;
        setSelectedSort(urlFilters.sortBy);
      }
      if (urlFilters.sortDirection) {
        newFilters.sortDirection = urlFilters.sortDirection;
        setSortDirection({ [urlFilters.sortBy]: urlFilters.sortDirection });
      }

      setFilters(prevFilters => ({
        ...prevFilters,
        ...newFilters
      }));
      setNewFilters(prevFilters => ({
        ...prevFilters,
        ...newFilters
      }));

      setHasAppliedFilters(Object.keys(newFilters).length > 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingWalkthroughs]);

  useEffect(() => {
    fetchFieldWalkthroughs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage, sortDirection, selectedSort, filters, showOnlyMine]);

  const isDeletable = (fieldWalkthrough: IFieldWalkthrough) => {
    if (fieldWalkthrough.walkthroughStatus === 'Complete') {
      return false;
    }
    if (fieldWalkthrough.walkthroughStatus === 'New') {
      return true;
    }
    if (
      fieldWalkthrough.walkthroughStatus !== 'Complete' &&
      fieldWalkthrough.walkthroughStatus !== 'New'
    ) {
      if (isAdmin || isProjectManager) {
        return true;
      }
      return false;
    }
    return false;
  };

  const [isDeleting, setIsDeleting] = useState(false);
  const handleDelete = async (fieldWalkthrough: IFieldWalkthrough) => {
    const result = window.confirm('Are you sure you want to delete this Walkthrough?');
    if (result) {
      setIsDeleting(true);
      try {
        await deleteFieldWalkthrough(fieldWalkthrough.fieldWalkthroughId);
        enqueueSnackbar(`Field Walkthrough deleted!`, {
          variant: 'success'
        });
        await fetchFieldWalkthroughs();
      } catch (error) {
        enqueueSnackbar(error?.Detail ?? 'Error deleting field walkthrough, please try again.', {
          variant: 'error'
        });
      } finally {
        setIsDeleting(false);
      }
    }
  };
  const columns = useMemo(() => {
    return [
      {
        Header: 'Neighborhood Description',
        accessor: 'neighborhoodDescription',
        sort: true,
        isServerSorted: selectedSort === 'NeighborhoodDescription',
        isServerSortedDesc: sortDirection.NeighborhoodDescription === 'Desc',
        handleClickColumn: () => handleClickColumn('NeighborhoodDescription')
      },
      {
        Header: 'Lot Number',
        accessor: 'lotNumber',
        sort: true,
        isServerSorted: selectedSort === 'LotNumber',
        isServerSortedDesc: sortDirection.LotNumber === 'Desc',
        handleClickColumn: () => handleClickColumn('LotNumber')
      },
      {
        Header: 'Plan Description',
        accessor: 'planDescription',
        sort: true,
        isServerSorted: selectedSort === 'PlanDescription',
        isServerSortedDesc: sortDirection.PlanDescription === 'Desc',
        handleClickColumn: () => handleClickColumn('PlanDescription')
      },
      {
        Header: 'Created On',
        accessor: 'createdOn',
        sort: true,
        isServerSorted: selectedSort === 'CreatedOn',
        isServerSortedDesc: sortDirection.CreatedOn === 'Desc',
        handleClickColumn: () => handleClickColumn('CreatedOn'),
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IFieldWalkthrough } };
        }) => {
          return <span>{original.createdOn ? formatDate(original.createdOn) : ''}</span>;
        }
      },
      {
        Header: 'Created By',
        accessor: 'createdBy',
        sort: true,
        isServerSorted: selectedSort === 'CreatedBy',
        isServerSortedDesc: sortDirection.CreatedBy === 'Desc',
        handleClickColumn: () => handleClickColumn('CreatedBy')
      },
      {
        Header: 'Status',
        accessor: 'walkthroughStatus',
        sort: false
      },
      {
        id: 'actions',
        accessor: '',
        Header: '',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IFieldWalkthrough } };
        }) => {
          return (
            <>
              <Button
                id="edit"
                color="primary"
                startIcon={<Edit />}
                onClick={() => {
                  history.push(
                    `/production/field-walkthroughs/${original.fieldWalkthroughId}?${queryString}`
                  );
                }}
              >
                Details
              </Button>
              {isDeletable(original) && (
                <Button
                  id="delete"
                  color="primary"
                  startIcon={<Delete />}
                  className={classes.deleteButton}
                  onClick={() => handleDelete(original)}
                >
                  Delete
                </Button>
              )}
              {original.walkthroughStatus === 'Complete' && hasRequiredRole && (
                <Button
                  id="edit"
                  color="secondary"
                  disabled={isResetting}
                  startIcon={<Restore />}
                  onClick={async () => {
                    setResetting(true);
                    try {
                      await resetFieldWalkthrough(original.fieldWalkthroughId);
                      enqueueSnackbar(`Field Walkthrough reset!`, {
                        variant: 'success'
                      });
                      fetchFieldWalkthroughs();
                    } catch (error) {
                      enqueueSnackbar(
                        error?.Detail ?? 'Error resetting field walkthrough, please try again.',
                        {
                          variant: 'error'
                        }
                      );
                    } finally {
                      setResetting(false);
                    }
                  }}
                >
                  Reset Walkthrough
                </Button>
              )}
            </>
          );
        }
      }
    ].filter(Boolean);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortDirection, selectedSort, queryString]);

  return (
    <OrderManagementPage title="Walkthrough List">
      <PageTitleWrapper>
        <PageTitle
          title="Walkthrough List"
          additionalHeaderContentClass={classes.titleWrapper}
          additionalHeaderContent={
            <Grid
              container
              flexWrap="wrap"
              spacing={1}
              //className={classes.buttonWrapper}
              display="flex"
              alignItems="center"
            >
              <Grid item xs={12} sm={'auto'} display="flex">
                <FormControlLabel
                  control={
                    <Switch
                      color="primary"
                      checked={showOnlyMine}
                      id="active-inactive"
                      disabled={isDeleting || isLoadingWalkthroughs}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setShowOnlyMine(e.target.checked);
                      }}
                    />
                  }
                  label="Show Only Mine"
                />
                {showToolbarDivider && <Divider orientation="vertical" flexItem />}
              </Grid>
              <Grid item>
                <Button
                  startIcon={<Add />}
                  color="primary"
                  variant="contained"
                  disabled={isDeleting || isLoadingWalkthroughs}
                  component={Link}
                  to="/production/field-walkthroughs/new"
                >
                  New Walkthrough
                </Button>
              </Grid>
            </Grid>
          }
        />
      </PageTitleWrapper>
      <FieldWalkthroughFilters
        isLoading={isDeleting || isLoadingWalkthroughs}
        hasAppliedFilters={hasAppliedFilters}
        setHasAppliedFilters={setHasAppliedFilters}
        toggleCompleted={toggleCompleted}
        isComplete={isComplete}
        applyFilters={(clearFilters?: boolean, clearSearch?: boolean) => {
          let paramFilters = {};
          if (clearFilters) {
            paramFilters = {
              ...filters,
              planDescription: null,
              territoryId: null,
              neighborhoodId: null,
              lotNumber: null,
              createdOnStart: null,
              createdOnEnd: null,
              createdBy: null,
              isComplete: false
            };
            setSelectedDateRange(undefined);
            setFilters({
              planDescription: null,
              territoryId: null,
              neighborhoodId: null,
              lotNumber: null,
              createdOnStart: null,
              createdOnEnd: null,
              createdBy: null,
              isComplete: false
            });
          } else {
            paramFilters = {
              ...filters,
              planDescription: searchValue,
              territoryId: selectedTerritory?.territoryId,
              neighborhoodId: selectedNeighborhood,
              lotNumber: selectedLotNumber,
              createdOnStart: selectedDateRange?.startDate?.toISOString() || undefined,
              createdOnEnd: selectedDateRange?.endDate?.toISOString() || undefined,
              createdBy: selectedCreatedBy,
              isComplete,
              page: page,
              sortBy: selectedSort,
              perPage: perPage,
              sortDirection: sortDirection[selectedSort]
            };
            setFilters({
              ...filters,
              planDescription: clearSearch ? '' : searchValue,
              territoryId: selectedTerritory?.territoryId,
              neighborhoodId: selectedNeighborhood,
              lotNumber: selectedLotNumber,
              createdOnStart: selectedDateRange?.startDate?.toISOString() || undefined,
              createdOnEnd: selectedDateRange?.endDate?.toISOString() || undefined,
              createdBy: selectedCreatedBy,
              isComplete
            });
          }
          setNewFilters(paramFilters);
          updateUrlWithFilters(paramFilters);
        }}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        selectedTerritory={selectedTerritory}
        setSelectedTerritory={setSelectedTerritory}
        selectedNeighborhood={selectedNeighborhood}
        setSelectedNeighborhood={setSelectedNeighborhood}
        selectedLotNumber={selectedLotNumber}
        setSelectedLotNumber={setSelectedLotNumber}
        selectedDateRange={selectedDateRange}
        setSelectedDateRange={setSelectedDateRange}
        selectedCreatedBy={selectedCreatedBy}
        setSelectedCreatedBy={setSelectedCreatedBy}
      />
      <Divider className={classes.divider} />
      <Table
        columns={columns}
        data={fieldWalkthroughs}
        isLoading={isDeleting || isLoadingWalkthroughs}
        ResponsiveComponent={isMobile ? MobileFieldWalkthroughs : undefined}
        mobileBreakPoint={1200}
        mobileProps={{
          handleDelete: (val: IFieldWalkthrough) => handleDelete(val),
          isDeletable: (val: IFieldWalkthrough) => isDeletable(val)
        }}
        hidePagination
      />
      {(!isDeleting || !isLoadingWalkthroughs) && (
        <Pagination
          page={page}
          count={recordCount}
          rowsPerPage={perPage}
          setPage={newPage => {
            setNewFilters({ ...newFilters, page: newPage + 1 });
            updateUrlWithFilters({ ...newFilters, page: newPage + 1 });
            setPage(newPage);
          }}
          setRowsPerPage={rowsPerPage => {
            setNewFilters({ ...newFilters, page: 1, perPage: rowsPerPage });
            updateUrlWithFilters({ ...newFilters, page: 1, perPage: rowsPerPage });
            setRowsPerPage(rowsPerPage);
            setPage(0);
          }}
        />
      )}
    </OrderManagementPage>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  divider: {
    margin: `${theme.spacing(1)} 0`
  },
  titleWrapper: {
    marginLeft: 0,
    width: '100%',
    [theme.breakpoints.up('md')]: {
      marginLeft: 'auto',
      width: 'auto'
    }
  },
  deleteButton: {
    marginLeft: theme.spacing(0.5),
    color: theme.palette.error.main
  }
}));
